import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import { UnionIcon, SubtractIcon, IntersectIcon, ExcludeIcon } from "./Primitives/icons";
import { DropdownChevronIcon } from "./Primitives/icons/DropdownChevronIcon";
import { Editor, TLShape, TLShapeId } from "@tldraw/tldraw";
import {
  performBooleanOperation,
  createMaskShape,
  enterMaskEditMode,
  exitMaskEditMode,
} from "../utils/booleanOperations";

interface MaskComponentProps {
  selectedShape: any;
  selectedShapes: TLShape[];
  selectedShapeIds: TLShapeId[];
  editor: Editor;
}

const maskOperations = [
  { id: "union", label: "Union", icon: UnionIcon },
  { id: "subtract", label: "Subtract", icon: SubtractIcon },
  { id: "intersect", label: "Intersect", icon: IntersectIcon },
  { id: "exclude", label: "Exclude", icon: ExcludeIcon },
];

const MaskComponent = ({ selectedShape, selectedShapes, selectedShapeIds, editor }: MaskComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState<(typeof maskOperations)[0] | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper to check if a shape is a QR code
  const isQRCode = (shape: TLShape | null | undefined) => {
    if (!shape) return false;
    const props = shape.props as any;
    return (
      shape.type === "customImage" &&
      (props?.qrCodeLink || shape.id.toString().includes("qrCode") || props?.assetId?.toString().includes("qr"))
    );
  };

  // Filter out QR codes and null/undefined shapes from selected shapes for masking
  const maskableShapes = selectedShapes.filter((s) => s && !isQRCode(s));
  const maskableShapeIds = maskableShapes.map((s) => s.id);

  // Detect if we're in a mask group (editing mode) or have the mask result shape selected
  const isMaskGroup =
    maskableShapeIds.length === 1 && selectedShape?.type === "group" && selectedShape?.meta?.isMaskGroup;
  const isMaskResultShape = maskableShapeIds.length === 1 && selectedShape?.meta?.isMaskShape; // Selected shape is the visible mask result
  const isInMaskGroup = selectedShape?.meta?.isInMaskGroup; // Inside a mask group
  const currentMaskOperation = isMaskGroup
    ? selectedShape.meta.maskOperation
    : isMaskResultShape
    ? selectedShape.meta.maskOperation
    : null;

  const handleMaskOperation = (operation: string) => {
    // Case 1: Editing existing mask group OR mask result shape selected (change operation)
    // Flow: enter edit mode → set new boolean operation on group → exit edit mode (recalculates with new op)
    if (isMaskGroup || isMaskResultShape) {
      const group = isMaskGroup
        ? selectedShape
        : (editor.getShape(selectedShape.meta.maskGroupId as TLShapeId) as TLShape | undefined);
      const maskShapeId = (isMaskGroup ? selectedShape.meta?.maskShapeId : selectedShape.id) as TLShapeId;

      if (!group || group.type !== "group") return;

      const childIds = editor.getSortedChildIdsForParent(group.id);
      const children = childIds
        .map((id) => editor.getShape(id))
        .filter((s): s is TLShape => !!s && !!s.meta?.isInMaskGroup);

      if (children.length < 2) return;

      const result = performBooleanOperation(
        children,
        operation as "union" | "subtract" | "intersect" | "exclude",
        editor
      );
      if (!result || result.isEmpty) {
        const operationLabel = operation.charAt(0).toUpperCase() + operation.slice(1);
        window.alert(
          `${operationLabel} operation resulted in no visible geometry.\n\n` +
            `The mask group will remain with its current operation.`
        );
        return;
      }

      enterMaskEditMode(maskShapeId, editor, { selectGroup: false });

      const groupInEditMode = editor.getShape(group.id as TLShapeId);
      if (groupInEditMode && groupInEditMode.type === "group") {
        editor.updateShape({
          ...groupInEditMode,
          meta: { ...groupInEditMode.meta, maskOperation: operation },
        });
      }

      exitMaskEditMode(maskShapeId, editor);
      return;
    }

    // Case 2: Creating new mask group
    if (maskableShapeIds.length >= 2) {
      // Perform the boolean operation
      const result = performBooleanOperation(maskableShapes, operation as any, editor);

      if (!result) {
        console.error("Boolean operation failed");
        return;
      }

      // UX: Warn user about empty results
      if (result.isEmpty) {
        const operationLabel = operation.charAt(0).toUpperCase() + operation.slice(1);
        const proceed = window.confirm(
          `${operationLabel} operation resulted in no visible geometry.\n\n` +
            `This happens when selected shapes don't have a common overlapping area.\n\n` +
            `Do you want to continue?`
        );

        if (!proceed) {
          return;
        }
      }
      // Create mask group + result shape (use maskableShapes, not selectedShapes)
      createMaskShape(maskableShapes, result.svgPath, result.bounds, operation, editor);
    }
  };

  const handleSelect = (operation: (typeof maskOperations)[0]) => {
    setSelectedOperation(operation);
    handleMaskOperation(operation.id);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Show dropdown if:
  // - 2 or more maskable shapes selected (create new mask), OR
  // - Mask result shape selected (change operation), OR
  // - Mask group selected (editing), OR
  // - Inside a mask group
  const shouldShow = maskableShapeIds.length >= 2 || isMaskResultShape || isMaskGroup || isInMaskGroup;

  if (!shouldShow) {
    return null;
  }

  const displayOperation =
    selectedOperation || (currentMaskOperation ? maskOperations.find((op) => op.id === currentMaskOperation) : null);

  return (
    <div className="px-4 mt-2" ref={dropdownRef}>
      <div className="flex flex-col gap-2 relative">
        {/* Custom Dropdown Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between cursor-pointer bg-white transition-colors"
          style={{
            padding: "4px 8px",
            borderRadius: "4px 4px 0 0",
            border: "1px solid #E2E2FC",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#E2E2FC";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "white";
          }}
        >
          <div className="flex items-center gap-2">
            {displayOperation ? (
              <>
                <displayOperation.icon />
                <span
                  style={{
                    color: "#2E3860",
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 400,
                    lineHeight: "16px",
                  }}
                >
                  {displayOperation.label}
                </span>
              </>
            ) : (
              <>
                <UnionIcon />
                <span
                  style={{
                    color: "#2E3860",
                    fontFamily: "Inter",
                    fontSize: "10px",
                    fontWeight: 400,
                    lineHeight: "16px",
                  }}
                >
                  Union
                </span>
              </>
            )}
          </div>
          <div className={cn("transition-transform", isOpen && "rotate-180")}>
            <DropdownChevronIcon style={{ position: "static" }} />
          </div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className="absolute top-full left-0 right-0 bg-white shadow-lg z-50"
            style={{
              border: "1px solid #E2E2FC",
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
            }}
          >
            {maskOperations.map((operation) => {
              const isSelected = displayOperation != null && operation.id === displayOperation.id;
              return (
                <button
                  key={operation.id}
                  onClick={() => handleSelect(operation)}
                  className="flex items-center gap-2 w-full transition-colors"
                  style={{
                    padding: "4px 8px",
                    backgroundColor: isSelected ? "#E2E2FC" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#E2E2FC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? "#E2E2FC" : "transparent";
                  }}
                >
                  <operation.icon />
                  <span
                    style={{
                      color: "#2E3860",
                      fontFamily: "Inter",
                      fontSize: "10px",
                      fontWeight: 400,
                      lineHeight: "16px",
                    }}
                  >
                    {operation.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaskComponent;
